import {
  APPOINTMENT_STATUSES,
  type AppointmentStatus,
  DEFAULT_APPOINTMENT_DURATION_MIN,
  DEFAULT_SERIES_INTERVAL_WEEKS,
  type LocationChoice,
  MAX_APPOINTMENT_TITLE_LENGTH,
  type Patient,
  VISIO_LOCATION,
  schoolYearEndDate,
} from '@ergo/shared';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, errorMessage } from '../api';
import {
  FREQUENCY_OPTIONS,
  STATUS_LABELS,
  VISIO_GLYPH,
  applySlotChange,
  defaultLocationChoice,
  frequencyLabel,
  locationChoiceOf,
  placesOf,
  removeAppointment,
  titleOf,
} from '../appointments';
import { DayPreview } from '../components/DayPreview';
import { Feasibility } from '../components/Feasibility';
import { HolidayHint } from '../components/HolidayHint';
import { PersoWarning } from '../components/PersoWarning';
import { OutcomeButtons } from '../components/OutcomeButtons';
import { ScopeDialog, type SeriesScope } from '../components/ScopeDialog';
import { Switch } from '../components/Switch';
import { useResource } from '../hooks';
import { useUnlogged } from '../unlogged';
import {
  WEEKDAY_OPTIONS,
  atTime,
  dateOf,
  endsAt,
  firstOccurrence,
  formatDate,
  formatLongDate,
  isValidDate,
  now,
  timeOf,
  today,
  weekdayOf,
} from '../time';

/** Where "Annuler" and a successful save go back to. */
function useReturnTo(): string {
  const state = useLocation().state as { from?: string } | null;
  return state?.from ?? '/aujourdhui';
}

export function RendezVous() {
  const params = useParams();
  return params.id ? <EditAppointment id={Number(params.id)} /> : <CreateAppointment />;
}

// ------------------------------------------------------------------- create

type Mode = 'unique' | 'serie';

function CreateAppointment() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = useReturnTo();

  const patients = useResource(() => api.patients.list({ active: true }), []);
  const settings = useResource(() => api.settings.get(), []);
  const unlogged = useUnlogged();

  const [mode, setMode] = useState<Mode>('unique');
  const [patientId, setPatientId] = useState<number | null>(
    search.get('patientId') ? Number(search.get('patientId')) : null,
  );
  const [locationId, setLocationId] = useState<LocationChoice | null>(null);
  // Un `?date=` se tape à la main : le calendrier refuse un jour qui n'existe pas
  // (un 30 février), et le formulaire s'ouvre alors sur aujourd'hui, comme ailleurs.
  const requestedDate = search.get('date');
  const initialDate = requestedDate && isValidDate(requestedDate) ? requestedDate : today();
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(search.get('time') ?? '09:00');
  const [durationMin, setDurationMin] = useState(DEFAULT_APPOINTMENT_DURATION_MIN);
  const [title, setTitle] = useState('');
  const [important, setImportant] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(() => initialOutcome(search.get('status')));
  const [weekday, setWeekday] = useState(() => weekdayOf(initialDate));
  const [intervalWeeks, setIntervalWeeks] = useState(DEFAULT_SERIES_INTERVAL_WEEKS);
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [created, setCreated] = useState<number | null>(null);

  const patient = useMemo(
    () => patients.data?.find((item) => item.id === patientId) ?? null,
    [patients.data, patientId],
  );

  // The server applies the same default; prefilling it keeps the form honest.
  useEffect(() => {
    if (settings.data) setDurationMin(settings.data.defaultDurationMin);
  }, [settings.data]);

  useEffect(() => {
    if (!patient) return;
    setLocationId((current) => defaultLocationChoice(patient, current));
  }, [patient]);

  function onDateChange(value: string) {
    setDate(value);
    if (isValidDate(value)) setWeekday(weekdayOf(value));
  }

  // A série starts on the first matching weekday on or after the start date:
  // that day, not the one in the field, is the one worth previewing.
  const previewDate =
    mode === 'serie' && isValidDate(date) ? firstOccurrence(date, weekday) : date;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (patientId === null || locationId === null) return;
    setPending(true);
    setError(null);

    try {
      if (mode === 'unique') {
        await api.appointments.create({
          patientId,
          locationId,
          startsAt: atTime(date, time),
          plannedDurationMin: durationMin,
          title,
          important,
          ...outcomePatch(outcome, durationMin),
        });
        unlogged.refresh();
        navigate(returnTo, { replace: true });
        return;
      }

      const result = await api.series.create({
        patientId,
        locationId,
        weekday,
        startTime: time,
        durationMin,
        intervalWeeks,
        startDate: date,
        title,
        important,
        ...(endDate ? { endDate } : {}),
      });
      setCreated(result.appointmentsCreated);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  if (created !== null) {
    const dayLabel = WEEKDAY_OPTIONS.find((option) => option.weekday === weekday)?.label ?? '';
    return (
      <section className="page page--narrow">
        <h1 className="page__title">Série créée</h1>
        <p className="page__lead">
          {created} séance{created > 1 ? 's' : ''} programmée{created > 1 ? 's' : ''},{' '}
          {intervalWeeks === 1 ? `chaque ${dayLabel}` : `un ${dayLabel} sur ${intervalWeeks}`} à{' '}
          {time}.
        </p>
        <div className="form__actions">
          <Link className="button button--primary" to={returnTo}>
            Retour à l’agenda
          </Link>
          <button type="button" className="button" onClick={() => setCreated(null)}>
            Créer une autre série
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="page page--narrow">
      <header className="page__header">
        <h1 className="page__title">Nouvelle séance</h1>
      </header>

      {patients.error && <p className="error" role="alert">{patients.error}</p>}

      <form className="form" onSubmit={onSubmit}>
        <div className="segmented" role="group" aria-label="Type de séance">
          <button
            type="button"
            className={`segmented__option${mode === 'unique' ? ' is-active' : ''}`}
            onClick={() => setMode('unique')}
          >
            Séance unique
          </button>
          <button
            type="button"
            className={`segmented__option${mode === 'serie' ? ' is-active' : ''}`}
            onClick={() => setMode('serie')}
          >
            Série hebdomadaire
          </button>
        </div>

        <label className="field">
          <span className="field__label">Patient</span>
          <select
            className="input"
            value={patientId ?? ''}
            onChange={(event) => setPatientId(event.target.value ? Number(event.target.value) : null)}
            required
          >
            <option value="">Choisir un patient…</option>
            {(patients.data ?? []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>

        <LocationField
          patient={patient}
          locationId={locationId}
          onChange={setLocationId}
        />

        {mode === 'serie' && (
          <div className="field-row">
            <label className="field">
              <span className="field__label">Jour de la semaine</span>
              <select
                className="input"
                value={weekday}
                onChange={(event) => setWeekday(Number(event.target.value))}
              >
                {WEEKDAY_OPTIONS.map((option) => (
                  <option key={option.weekday} value={option.weekday}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <FrequencyField value={intervalWeeks} onChange={setIntervalWeeks} />
          </div>
        )}

        <div className="field-row">
          <label className="field">
            <span className="field__label">{mode === 'serie' ? 'À partir du' : 'Date'}</span>
            <input
              className="input"
              type="date"
              value={date}
              onChange={(event) => onDateChange(event.target.value)}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Heure</span>
            <input
              className="input"
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              required
            />
          </label>
        </div>

        <DurationField value={durationMin} onChange={setDurationMin} />

        <TitleField value={title} onChange={setTitle} />

        <Switch
          label="Rendez-vous important"
          hint="ESS, réunion… : en gras et bien visible dans l’agenda."
          value={important}
          onChange={setImportant}
        />

        <Feasibility query={{ locationId, date, time, durationMin }} />

        {/* Une série se prévient sur sa première occurrence : c'est le jour montré. */}
        <PersoWarning date={previewDate} time={time} durationMin={durationMin} />
        <HolidayHint date={previewDate} />

        <DayPreview
          query={{
            locationId,
            patientId,
            date: previewDate,
            time,
            durationMin,
            series: mode === 'serie',
          }}
        />

        {mode === 'unique' && (
          <OutcomeFields value={outcome} plannedDurationMin={durationMin} onChange={setOutcome} />
        )}

        {mode === 'serie' && (
          <label className="field">
            <span className="field__label">Jusqu’au (facultatif)</span>
            <input
              className="input"
              type="date"
              value={endDate}
              min={date}
              onChange={(event) => setEndDate(event.target.value)}
            />
            {isValidDate(date) && (
              <span className="hint">
                Laissé vide : jusqu’au {formatDate(schoolYearEndDate(date))}, fin de l’année
                scolaire.
              </span>
            )}
          </label>
        )}

        {error && <p className="error" role="alert">{error}</p>}

        <div className="form__actions">
          <button
            className="button button--primary"
            type="submit"
            disabled={pending || patientId === null || locationId === null}
          >
            {pending ? 'Enregistrement…' : mode === 'serie' ? 'Créer la série' : 'Créer la séance'}
          </button>
          <Link className="button button--ghost" to={returnTo}>
            Annuler
          </Link>
        </div>
      </form>
    </section>
  );
}

// --------------------------------------------------------------------- edit

type PendingAction = 'save' | 'delete';

function EditAppointment({ id }: { id: number }) {
  const navigate = useNavigate();
  const returnTo = useReturnTo();
  const unlogged = useUnlogged();

  const loaded = useResource(async () => {
    const appointment = await api.appointments.get(id);
    const patient = await api.patients.get(appointment.patientId);
    const series = appointment.seriesId === null ? null : await api.series.get(appointment.seriesId);
    return { appointment, patient, series };
  }, [id]);

  const [locationId, setLocationId] = useState<LocationChoice | null>(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [durationMin, setDurationMin] = useState(DEFAULT_APPOINTMENT_DURATION_MIN);
  const [intervalWeeks, setIntervalWeeks] = useState(DEFAULT_SERIES_INTERVAL_WEEKS);
  const [title, setTitle] = useState('');
  const [important, setImportant] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>({ status: 'planned', actualDurationMin: null });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [scopeFor, setScopeFor] = useState<PendingAction | null>(null);

  useEffect(() => {
    if (!loaded.data) return;
    const { appointment, patient, series } = loaded.data;
    setLocationId(locationChoiceOf(patient, appointment.locationId));
    setDate(dateOf(appointment.startsAt));
    setTime(timeOf(appointment.startsAt));
    setDurationMin(appointment.plannedDurationMin);
    setIntervalWeeks(series?.intervalWeeks ?? DEFAULT_SERIES_INTERVAL_WEEKS);
    setTitle(appointment.title ?? '');
    setImportant(appointment.important);
    setOutcome({
      status: appointment.status,
      actualDurationMin: appointment.actualDurationMin,
    });
  }, [loaded.data]);

  async function save(scope: SeriesScope) {
    if (!loaded.data || locationId === null) return;
    setPending(true);
    setError(null);
    try {
      // The outcome belongs to this séance alone, never to the future ones a
      // "toutes les séances à venir" save would recreate. L'étoile et l'intitulé non
      // plus : ils se posent sur ce rendez-vous, jamais sur la série entière.
      const { appointment } = loaded.data;
      if (important !== appointment.important || title !== (appointment.title ?? '')) {
        await api.appointments.update(appointment.id, { important, title });
      }
      if (
        outcome.status !== appointment.status ||
        outcome.actualDurationMin !== appointment.actualDurationMin
      ) {
        await api.appointments.update(appointment.id, outcomePatch(outcome, durationMin));
        unlogged.refresh();
      }

      await applySlotChange(
        appointment,
        {
          startsAt: atTime(date, time),
          plannedDurationMin: durationMin,
          // Un lieu inchangé repart tel quel : une visio garde sa ligne, même en double.
          locationId:
            locationChoiceOf(loaded.data.patient, appointment.locationId) === locationId
              ? appointment.locationId
              : locationId,
          intervalWeeks,
        },
        scope,
      );
      navigate(returnTo, { replace: true });
    } catch (cause) {
      setError(errorMessage(cause));
      setScopeFor(null);
      setPending(false);
    }
  }

  async function remove(scope: SeriesScope) {
    if (!loaded.data) return;
    setPending(true);
    setError(null);
    try {
      await removeAppointment(loaded.data.appointment, scope);
      unlogged.refresh();
      navigate(returnTo, { replace: true });
    } catch (cause) {
      setError(errorMessage(cause));
      setScopeFor(null);
      setPending(false);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!loaded.data) return;
    if (loaded.data.appointment.seriesId === null) void save('one');
    else setScopeFor('save');
  }

  function onDelete() {
    if (!loaded.data) return;
    if (loaded.data.appointment.seriesId !== null) {
      setScopeFor('delete');
      return;
    }
    confirmDeleteOne();
  }

  /** From a past séance, deleting never touches the series. */
  function confirmDeleteOne() {
    if (window.confirm('Supprimer cette séance ? Elle ne laissera aucune trace.')) void remove('one');
  }

  function onLogged() {
    loaded.reload();
    unlogged.refresh();
  }

  if (loaded.error) {
    return (
      <section className="page">
        <p className="error" role="alert">{loaded.error}</p>
        <Link className="button" to={returnTo}>Retour</Link>
      </section>
    );
  }
  if (!loaded.data) return <p className="muted">Chargement…</p>;

  const { appointment, patient, series } = loaded.data;
  const seriesLabel = series ? `série ${frequencyLabel(series.intervalWeeks)}` : 'série';
  // Past once the planned end has gone by — and it stays past even if she reverts
  // the outcome to « Prévue ». All that is left to do on it is the pointage.
  const isPast = endsAt(appointment.startsAt, appointment.plannedDurationMin) <= now();

  const header = (
    <header className="page__header">
      <div>
        <Link className="backlink" to={returnTo}>← Retour</Link>
        <h1 className={`page__title${appointment.important ? ' is-important' : ''}`}>
          {titleOf(appointment)}
        </h1>
        <p className="page__lead">
          {formatLongDate(dateOf(appointment.startsAt))}
          {series && <span className="badge">Série {frequencyLabel(series.intervalWeeks)}</span>}
          {appointment.status !== 'planned' && (
            <span className="badge badge--muted">{STATUS_LABELS[appointment.status]}</span>
          )}
        </p>
      </div>
    </header>
  );

  if (isPast) {
    return (
      <section className="page page--narrow">
        {header}

        <div className="panel">
          <h2 className="panel__title">Pointage</h2>
          <OutcomeButtons appointment={appointment} onLogged={onLogged} />
        </div>

        <fieldset className="fieldset fieldset--locked" disabled>
          <legend className="fieldset__legend">Créneau (non modifiable)</legend>

          <LocationField patient={patient} locationId={locationId} onChange={setLocationId} />

          <div className="field-row">
            <label className="field">
              <span className="field__label">Date</span>
              <input className="input" type="date" value={date} readOnly />
            </label>
            <label className="field">
              <span className="field__label">Heure</span>
              <input className="input" type="time" value={time} readOnly />
            </label>
          </div>

          {series && <FrequencyField value={intervalWeeks} onChange={setIntervalWeeks} />}

          <DurationField value={durationMin} onChange={setDurationMin} />
        </fieldset>

        <p className="hint">
          Séance passée : le créneau ne se modifie plus, seul le pointage compte.
        </p>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="form__actions">
          <button
            type="button"
            className="button button--danger"
            disabled={pending}
            onClick={confirmDeleteOne}
          >
            Supprimer cette séance
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="page page--narrow">
      {header}

      <form className="form" onSubmit={onSubmit}>
        <LocationField patient={patient} locationId={locationId} onChange={setLocationId} />

        <div className="field-row">
          <label className="field">
            <span className="field__label">Date</span>
            <input
              className="input"
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Heure</span>
            <input
              className="input"
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              required
            />
          </label>
        </div>

        {series && (
          <FrequencyField
            value={intervalWeeks}
            onChange={setIntervalWeeks}
            hint="Appliquée aux séances à venir seulement, si vous modifiez toute la série."
          />
        )}

        <DurationField value={durationMin} onChange={setDurationMin} />

        <TitleField value={title} onChange={setTitle} />

        <Switch
          label="Rendez-vous important"
          hint="ESS, réunion… : en gras et bien visible dans l’agenda."
          value={important}
          onChange={setImportant}
        />

        <Feasibility query={{ locationId, date, time, durationMin, excludeId: appointment.id }} />

        <PersoWarning date={date} time={time} durationMin={durationMin} />
        <HolidayHint date={date} />

        <DayPreview
          query={{
            locationId,
            patientId: patient.id,
            date,
            time,
            durationMin,
            excludeId: appointment.id,
          }}
        />

        <OutcomeFields value={outcome} plannedDurationMin={durationMin} onChange={setOutcome} />

        {error && <p className="error" role="alert">{error}</p>}

        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <Link className="button button--ghost" to={returnTo}>
            Annuler
          </Link>
          <button type="button" className="button button--danger" disabled={pending} onClick={onDelete}>
            Supprimer
          </button>
        </div>
      </form>

      <p className="hint">
        Le patient d’une séance ne change pas : supprimez celle-ci et créez-en une autre si besoin.
      </p>

      {scopeFor && (
        <ScopeDialog
          title={scopeFor === 'save' ? 'Modifier la série' : 'Supprimer la séance'}
          description={
            scopeFor === 'save'
              ? `Cette séance fait partie d’une ${seriesLabel}.`
              : `Cette séance fait partie d’une ${seriesLabel}. Les séances déjà pointées sont conservées.`
          }
          oneLabel="Seulement cette séance"
          futureLabel="Toutes les séances à venir"
          pending={pending}
          onChoose={(scope) => void (scopeFor === 'save' ? save(scope) : remove(scope))}
          onCancel={() => setScopeFor(null)}
        />
      )}
    </section>
  );
}

// ------------------------------------------------------------ shared fields

function LocationField({
  patient,
  locationId,
  onChange,
}: {
  patient: Patient | null;
  locationId: LocationChoice | null;
  onChange: (value: LocationChoice) => void;
}) {
  const places = patient ? placesOf(patient) : [];

  return (
    <label className="field">
      <span className="field__label">Lieu</span>
      <select
        className="input"
        value={locationId ?? ''}
        onChange={(event) =>
          onChange(
            event.target.value === VISIO_LOCATION ? VISIO_LOCATION : Number(event.target.value),
          )
        }
        disabled={!patient}
        required
      >
        {!patient && <option value="">Choisir d’abord un patient</option>}
        {places.map((location) => (
          <option key={location.id} value={location.id}>
            {location.label} — {location.address}
          </option>
        ))}
        {patient && (
          <option value={VISIO_LOCATION}>{VISIO_GLYPH} Visio (depuis la maison)</option>
        )}
      </select>
      {patient && places.length === 0 && (
        <span className="hint">
          {patient.name} n’a aucun lieu de visite.{' '}
          <Link to={`/patients/${patient.id}`}>Ajouter un lieu</Link>
        </span>
      )}
    </label>
  );
}

function FrequencyField({
  value,
  onChange,
  hint,
}: {
  value: number;
  onChange: (value: number) => void;
  hint?: string;
}) {
  return (
    <label className="field">
      <span className="field__label">Fréquence</span>
      <select
        className="input"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      >
        {FREQUENCY_OPTIONS.map((option) => (
          <option key={option.intervalWeeks} value={option.intervalWeeks}>
            {option.label}
          </option>
        ))}
      </select>
      {hint && <span className="hint">{hint}</span>}
    </label>
  );
}

/**
 * Le mot qui nomme la séance quand le nom du patient ne suffit pas. Facultatif, et
 * sans effet sur ce que la séance vaut : il se lit dans l'agenda, et nulle part
 * ailleurs — ni sur la note, ni dans le flux iCal.
 */
function TitleField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="field">
      <span className="field__label">Intitulé (facultatif)</span>
      <input
        className="input"
        type="text"
        value={value}
        maxLength={MAX_APPOINTMENT_TITLE_LENGTH}
        placeholder="ESS, bilan, réunion…"
        onChange={(event) => onChange(event.target.value)}
      />
      <span className="hint">Il passe devant le nom du patient : « ESS – André ».</span>
    </label>
  );
}

const DURATION_CHOICES = [30, 45, 60];

function DurationField({
  value,
  onChange,
  label = 'Durée prévue',
}: {
  value: number;
  onChange: (value: number) => void;
  label?: string;
}) {
  return (
    <div className="field">
      <span className="field__label">{label}</span>
      <div className="duration">
        {DURATION_CHOICES.map((choice) => (
          <button
            key={choice}
            type="button"
            className={`chip${value === choice ? ' is-active' : ''}`}
            onClick={() => onChange(choice)}
          >
            {choice} min
          </button>
        ))}
        <input
          className="input input--number"
          type="number"
          min={5}
          max={600}
          step={5}
          value={value}
          aria-label={`${label} en minutes`}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        <span className="muted">min</span>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ outcome

interface Outcome {
  status: AppointmentStatus;
  actualDurationMin: number | null;
}

/** Pointage came in from a notification or the Pointage "+": start on « Effectuée ». */
function initialOutcome(requested: string | null): Outcome {
  const status = APPOINTMENT_STATUSES.find((candidate) => candidate === requested);
  return { status: status ?? 'planned', actualDurationMin: null };
}

/**
 * The billed duration lives and dies with « Effectuée », as the server enforces —
 * sending one on another statut is a 422. An outcome prefilled from a link has no
 * duration yet: the planned one is what the field shows, so it is also what gets sent.
 */
function outcomePatch(outcome: Outcome, plannedDurationMin: number) {
  return outcome.status === 'done'
    ? {
        status: outcome.status,
        actualDurationMin: outcome.actualDurationMin ?? plannedDurationMin,
      }
    : { status: outcome.status, actualDurationMin: null };
}

function OutcomeFields({
  value,
  plannedDurationMin,
  onChange,
}: {
  value: Outcome;
  plannedDurationMin: number;
  onChange: (value: Outcome) => void;
}) {
  function onStatusChange(status: AppointmentStatus) {
    onChange({
      status,
      actualDurationMin:
        status === 'done' ? (value.actualDurationMin ?? plannedDurationMin) : null,
    });
  }

  return (
    <fieldset className="fieldset">
      <legend className="fieldset__legend">Pointage</legend>

      <label className="field">
        <span className="field__label">Statut</span>
        <select
          className="input"
          value={value.status}
          onChange={(event) => onStatusChange(event.target.value as AppointmentStatus)}
        >
          {APPOINTMENT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {STATUS_LABELS[status]}
            </option>
          ))}
        </select>
      </label>

      {value.status === 'done' && (
        <DurationField
          label="Durée réelle (facturée)"
          value={value.actualDurationMin ?? plannedDurationMin}
          onChange={(actualDurationMin) => onChange({ ...value, actualDurationMin })}
        />
      )}
    </fieldset>
  );
}
